// FilterData.tsx
import React, { useEffect, useMemo, useState } from 'react';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { Button, FormControl, Tabs, Tab, SelectChangeEvent } from '@mui/material';
import { ExpandMore } from '@mui/icons-material';
import { IconArrowRight } from '../../../icons/Icon';
import SingleSelect from '../../../components/commonComponent/SingleSelect';
import styles from './plansetup.module.scss';
import CustomTabPanel from '../../../components/commonComponent/CustomTabPanel';
import { useDispatch, useSelector } from 'react-redux';
import { RootState, AppDispatch } from '../../../store/store';
import { fetchSectors, fetchAllRegions, fetchAllSites } from '../../../services/sectorRegionAllSitesSlice';

interface PlanProps {
  handleClick: () => void;
  handleFilterChange: () => void;
}

const FilterData: React.FC<PlanProps> = ({ handleClick, handleFilterChange }) => {
  const [expanded, setExpanded] = useState<boolean>(true);
  const [selectedOptions, setSelectedOptions] = useState({ sector: '', region: '' });
  const [selectedLabels, setSelectedLabels] = useState({ sector: '', region: '' });
  const [enableSubmit, setEnableSubmit] = useState<boolean>(false);
  const dispatch = useDispatch<AppDispatch>();
  const userregions = useSelector((state: RootState) => state.sectorRegionAllSites.regions);
  const usersectors = useSelector((state: RootState) => state.sectorRegionAllSites.sectors);

  useEffect(() => {
    dispatch(fetchSectors());
  }, [dispatch]);

  useEffect(() => {
    if (selectedOptions.sector) {
      dispatch(fetchAllRegions(selectedOptions.sector));
    }
  }, [dispatch, selectedOptions.sector]);

  const regionOptions = useMemo(
    () =>
      userregions?.map((regionItem: any) => ({
        label: regionItem?.regionname,
        value: regionItem?.regionid,
      })),
    [userregions]
  );

  const sectorOptions = useMemo(
    () =>
      usersectors?.map((sectorItem: any) => ({
        label: sectorItem?.sectorname,
        value: sectorItem?.sectorcode,
      })),
    [usersectors]
  );

  const handleChange = (field: 'sector' | 'region') => (event: SelectChangeEvent<string | number>) => {
    const value = event.target.value;
    setSelectedOptions((prev: any) => ({
      ...prev,
      [field]: value,
    }));
    handleFilterChange();
  };

  useEffect(() => {
    setEnableSubmit(Boolean(selectedOptions.sector && selectedOptions.region));
  }, [selectedOptions]);

  // Auto-select the region if there's only one region available
  useEffect(() => {
    if (regionOptions.length === 1 && selectedOptions.sector !== '' && selectedOptions.region !== regionOptions[0].value) {
      setSelectedOptions((prev) => ({
        ...prev,
        region: regionOptions[0].value,
      }));
    }
    else {
      setSelectedOptions((prev) => ({
        ...prev,
        region: '',
      }));
    }
  }, [regionOptions, selectedOptions.sector]);

  const handleSubmit = async () => {
    handleClick(); // Trigger fetch in parent component
    setExpanded(!expanded);
    setSelectedLabels({
      sector:
        usersectors.find((sector: any) => selectedOptions.sector === sector.sectorcode)?.sectorname || '',
      region:
        userregions.find((region: any) => selectedOptions.region === region.regionid)?.regionname || '',
    });
    if (selectedOptions.sector && selectedOptions.region) {
      // Fetch sites only when Go button is clicked
      dispatch(fetchAllSites({
        sector: selectedOptions.sector,
        region: selectedOptions.region
      }));
    }
  };

  return (
    <div className="white-box mb-12">
      <div className={`flex justify-content-between align-items-center ${expanded ? styles.tabbingDataWrap : styles.tabbingDataWrapMinimize}`}>
        {expanded ? (
          <Tabs value={0} aria-label="Tabs" className={!expanded ? 'tabs-wrapper colapsed' : 'tabs-wrapper'}>
            <Tab label="Filter" id={`simple-tabpanel-${0}`} aria-labelledby={`simple-tab-${0}`} disableRipple />
          </Tabs>
        ) : (
          <div className="flex align-items-center">
            <div className={`selecte-sites flex align-items-center justify-content-start ${styles.selectedSites}`}>
              <span className={styles.dateLabel}>Sector:</span>
              <span className={styles.chipData}>{selectedLabels.sector}</span>
            </div>
            <div className={`selecte-sites flex align-items-center justify-content-start ${styles.selectedSites}`}>
              <span className={styles.dateLabel}>Region:</span>
              <span className={styles.chipData}>{selectedLabels.region}</span>
            </div>
          </div>
        )}
        <Button onClick={() => setExpanded(!expanded)} className={`expend-btn ${expanded && 'active'}`} disableRipple>
          {expanded ? 'Minimize Section' : 'Expand'}
          <span>
            <ExpandMore />
          </span>
        </Button>
      </div>
      <div className={`inner-contant ${!expanded && 'd-none'}`}>
        <CustomTabPanel value={0} index={0}>
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <FormControl fullWidth>
              <div className="flex justify-content-between align-items-end">
                <div className={`${styles.leftBox} flex justify-content-between align-items-end`}>
                  <div className={`flex flex-column ${styles.multiSelect}`}>
                    <SingleSelect
                      label="Select Sector"
                      options={sectorOptions}
                      value={selectedOptions.sector || ''}
                      onChange={handleChange('sector')}
                      placeholder="Select Sector"
                    />
                  </div>
                  <div className={`flex flex-column ${styles.multiSelect}`}>
                    <SingleSelect
                      label="Region"
                      options={regionOptions}
                      value={selectedOptions.region || ''}
                      onChange={handleChange('region')}
                      placeholder="Select Region"
                      disabled={!selectedOptions.sector || regionOptions.length === 1}
                    />
                  </div>
                </div>
                <Button onClick={handleSubmit} disabled={!enableSubmit} className={`primary-btn ${styles.primaryBtn}`}>
                  Go <IconArrowRight />
                </Button>
              </div>
            </FormControl>
          </LocalizationProvider>
        </CustomTabPanel>
      </div>
    </div>
  );
};

export default FilterData;
